import { ESPECES, TYPES_SIGNALEMENT } from '../domaine/types'
import { ESPECE_LIBELLE, SIGNALEMENT_LIBELLE, T } from '../domaine/libelles'
import { surEvenement } from '../noyau/bus'
import { csvLocal, nomFichier, telecharger, type TableExport } from '../noyau/csv'
import { rafraichirTotaux, totauxEnCache, totauxLocaux } from '../noyau/saison'
import { el, vibrer, vider } from '../ui/dom'
import { montrerToast } from '../ui/toast'

/*
  Écran Saison.

  Lecture depuis le cache : fonctionne hors réseau, avec la mention explicite
  de la date de dernière synchronisation. Aucun classement, aucun badge,
  aucun score entre bénévoles — seulement les chiffres de la saison.
*/

function dateLisible(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function monterSaison(hote: HTMLElement): () => void {
  let vivant = true
  const off = surEvenement('saison-changee', () => void rendre())

  async function rendre(): Promise<void> {
    if (!vivant) return

    const cache = await totauxEnCache()
    const locaux = await totauxLocaux()
    if (!vivant) return

    vider(hote)

    hote.append(
      el('section', { class: 'entete-simple' }, [
        el('h1', { class: 'entete-simple__titre', text: cache?.saison ?? T.saisonTitre }),
        el('p', {
          class: 'entete-simple__aide',
          text: cache
            ? `${T.derniereSynchro} · ${dateLisible(cache.recu_le)}`
            : T.jamaisSynchronise,
        }),
      ]),
    )

    if (cache) {
      hote.append(
        section(T.pontesParEspece, ESPECES.map((e) => [ESPECE_LIBELLE[e], cache.pontes[e]])),
        section(T.autresObservations, [
          ['Traces sans ponte', cache.traces_sans_ponte],
          ['Émergences', cache.emergences],
          ['Personnes sensibilisées', cache.personnes_sensibilisees],
        ]),
        section(
          T.signalementsTitre,
          TYPES_SIGNALEMENT.map((t) => [SIGNALEMENT_LIBELLE[t], cache.signalements[t]]),
        ),
      )
    }

    hote.append(
      el('p', { class: 'note-locale', text: T.totauxLocaux }),
      section('Cet appareil', [
        ...ESPECES.map((e) => [`Pontes · ${ESPECE_LIBELLE[e]}`, locaux.pontes[e]] as [string, number]),
        ['Traces sans ponte', locaux.traces_sans_ponte],
        ['Émergences', locaux.emergences],
        ['Personnes sensibilisées', locaux.personnes_sensibilisees],
      ]),
      blocExport(),
    )

    // Rafraîchissement opportuniste : jamais bloquant, jamais annoncé.
    void rafraichirTotaux()
  }

  /*
    Export local.

    Il ne contient que les saisies de cet appareil — c'est dit explicitement,
    parce qu'un fichier nommé « observations » qu'on croit complet alors qu'il
    ne l'est pas serait pire que pas de fichier du tout au moment du bilan.
  */
  function blocExport(): HTMLElement {
    const bouton = (texte: string, table: TableExport) => {
      const b = el('button', { type: 'button', class: 'champ-bouton', text: texte })
      b.addEventListener('click', () => {
        vibrer(15)
        void csvLocal(table).then((contenu) => {
          // Un CSV réduit à sa ligne d'en-têtes n'a aucun intérêt, et
          // téléchargerait un fichier vide sans le dire.
          if (contenu.split('\r\n').filter(Boolean).length <= 1) {
            montrerToast(T.exportVide)
            return
          }
          telecharger(nomFichier(table), contenu)
          montrerToast(T.exportFait)
        })
      })
      return b
    }

    return el('section', { class: 'bloc-saison bloc-saison--export' }, [
      el('h2', { class: 'bloc-saison__titre', text: T.exporterTitre.toUpperCase() }),
      el('p', { class: 'champ__aide', text: T.exporterAide }),
      el('div', { class: 'bloc-export' }, [
        bouton(T.exporterObservations, 'observations'),
        bouton(T.exporterSignalements, 'signalements'),
      ]),
    ])
  }

  function section(titre: string, lignes: Array<[string, number]>): HTMLElement {
    return el('section', { class: 'bloc-saison' }, [
      el('h2', { class: 'bloc-saison__titre', text: titre.toUpperCase() }),
      el(
        'div',
        { class: 'bloc-saison__lignes' },
        lignes.map(([libelle, valeur]) =>
          el('div', { class: 'ligne-saison' }, [
            el('span', { class: 'ligne-saison__libelle', text: libelle }),
            el('span', { class: 'ligne-saison__valeur', text: String(valeur) }),
          ]),
        ),
      ),
    ])
  }

  void rendre()

  return () => {
    vivant = false
    off()
  }
}
